import {
  type EditorConfig,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
} from 'lexical';

import { WordingNode, type WordingRun } from './WordingNode';

type SerializedNoteChoiceValueNode = Spread<
  { blockId: string; separator: string } & WordingRun,
  SerializedTextNode
>;

const markSeparator = (dom: HTMLElement, separator: string) => {
  if (separator) {
    dom.dataset.separator = separator;
  } else {
    delete dom.dataset.separator;
  }
};

// One chosen value of a choice block, printed as text of the document and
// typed over in place. Its run's segment is the value's place among the
// block's chosen values; the separator before it (", " or the language's
// "and") is drawn, never typed.
export class NoteChoiceValueNode extends WordingNode {
  __separator: string;

  static getType(): string {
    return 'note-choice-value';
  }

  static clone(node: NoteChoiceValueNode): NoteChoiceValueNode {
    return new NoteChoiceValueNode(
      node.__blockId,
      node.__run,
      node.__separator,
      node.__text,
      node.__key,
    );
  }

  static importJSON(json: SerializedNoteChoiceValueNode): NoteChoiceValueNode {
    const { blockId, text, segment, marked, tone, spaceBefore, separator } =
      json;
    return $createNoteChoiceValueNode({
      id: blockId,
      text,
      run: { segment, marked, tone, spaceBefore },
      separator,
    }).updateFromJSON(json);
  }

  constructor(
    blockId = '',
    run?: WordingRun,
    separator = '',
    text = '',
    key?: NodeKey,
  ) {
    super(blockId, run, text, key);
    this.__separator = separator;
  }

  exportJSON(): SerializedNoteChoiceValueNode {
    return {
      ...super.exportJSON(),
      separator: this.getSeparator(),
    };
  }

  createDOM(config: EditorConfig, editor?: LexicalEditor): HTMLElement {
    const dom = super.createDOM(config, editor);
    delete dom.dataset.noteWording;
    dom.dataset.noteChoiceValue = '';
    markSeparator(dom, this.__separator);
    return dom;
  }

  updateDOM(previous: this, dom: HTMLElement, config: EditorConfig): boolean {
    const replace = super.updateDOM(previous, dom, config);
    if (!replace && previous.__separator !== this.__separator) {
      markSeparator(dom, this.__separator);
    }
    return replace;
  }

  getSeparator(): string {
    return this.getLatest().__separator;
  }

  setSeparator(separator: string): this {
    const writable = this.getWritable();
    writable.__separator = separator;
    return writable;
  }
}

export const $createNoteChoiceValueNode = ({
  id,
  text,
  run,
  separator,
}: {
  id: string;
  text: string;
  run: WordingRun;
  separator: string;
}) => new NoteChoiceValueNode(id, run, separator, text);

export const $isNoteChoiceValueNode = (
  node: LexicalNode | null | undefined,
): node is NoteChoiceValueNode => node instanceof NoteChoiceValueNode;
